import {
  SCHEME_DISPLAY_ZOOM_MAX,
  SCHEME_DISPLAY_ZOOM_MIN,
} from '../constants/reactionScheme';

/** Sidebar “Font size” options (interpreted as pt; 12 pt ≈ 16 px at 96 dpi; 12 = reference at 100% zoom). */
export const SCHEME_FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24] as const;

export type SchemeFontSize = (typeof SCHEME_FONT_SIZES)[number];

export const SCHEME_FONT_SIZE_DEFAULT: SchemeFontSize = 12;

export const SCHEME_ZOOM_PERCENT_DEFAULT = 100;

/** CSS px for a point size at 96 dpi (browser/CSS convention: 1 pt = 96/72 px; 12 pt → 16 px). */
export function schemeFontSizePtToCssPx(pt: number): number {
  return (pt * 96) / 72;
}

/** One-line conversion for the build preview (sidebar value is interpreted as pt). */
export function formatSchemeFontSizeConversion(fontSize: number): string {
  const px = schemeFontSizePtToCssPx(fontSize);
  const pxStr =
    Math.abs(px - Math.round(px)) < 1e-6 ? String(Math.round(px)) : px.toFixed(1);
  return `${fontSize} = ${fontSize} pt ≈ ${pxStr} px (96 dpi)`;
}

/** UI bounds for the percent field (integers only). */
export const SCHEME_ZOOM_PERCENT_UI_MIN = 0;
export const SCHEME_ZOOM_PERCENT_UI_MAX = 300;

const FONT_SET = new Set<number>(SCHEME_FONT_SIZES);

/**
 * Allowed integer percent range for a given font size so that
 * percent/100 stays within [SCHEME_DISPLAY_ZOOM_MIN, SCHEME_DISPLAY_ZOOM_MAX]
 * while also respecting SCHEME_ZOOM_PERCENT_UI_MIN/MAX.
 */
export function getSchemeZoomPercentBounds(_fontSize: number): { min: number; max: number } {
  const rawMin = SCHEME_DISPLAY_ZOOM_MIN * 100;
  const rawMax = SCHEME_DISPLAY_ZOOM_MAX * 100;
  const min = Math.max(SCHEME_ZOOM_PERCENT_UI_MIN, Math.ceil(rawMin - 1e-9));
  const max = Math.min(SCHEME_ZOOM_PERCENT_UI_MAX, Math.floor(rawMax + 1e-9));
  return min <= max ? { min, max } : { min: SCHEME_ZOOM_PERCENT_UI_MIN, max: SCHEME_ZOOM_PERCENT_UI_MAX };
}

export function clampSchemeZoomPercent(fontSize: number, percent: number): number {
  const { min, max } = getSchemeZoomPercentBounds(fontSize);
  const n = Math.round(Number(percent));
  if (!Number.isFinite(n)) return SCHEME_ZOOM_PERCENT_DEFAULT;
  return Math.min(max, Math.max(min, n));
}

/** effectiveScale = percent/100, clamped to export limits. */
export function computeEffectiveSchemeDisplayZoom(_fontSize: number, percent: number): number {
  const raw = percent / 100;
  return Math.min(SCHEME_DISPLAY_ZOOM_MAX, Math.max(SCHEME_DISPLAY_ZOOM_MIN, raw));
}

/**
 * `applyZoomToViewBox` divides by `scale`; 0 and non-finite values would break viewBox math.
 * Use a tiny positive value so 0% zoom still yields a valid (extremely zoomed-out) viewport.
 */
export const SCHEME_VIEWBOX_ZOOM_MATH_MIN = 1e-6;

export function schemeViewBoxZoomFactorForApply(scale: number): number {
  if (!Number.isFinite(scale)) return SCHEME_VIEWBOX_ZOOM_MATH_MIN;
  if (scale <= 0) return SCHEME_VIEWBOX_ZOOM_MATH_MIN;
  return scale;
}

export function isSchemeFontSize(n: unknown): n is SchemeFontSize {
  return typeof n === 'number' && Number.isFinite(n) && FONT_SET.has(n);
}

/** Nearest allowed font size to `ideal` (ties → smaller size). */
export function snapSchemeFontSize(ideal: number): SchemeFontSize {
  let best: SchemeFontSize = SCHEME_FONT_SIZES[0];
  let bestDiff = Infinity;
  for (const s of SCHEME_FONT_SIZES) {
    const d = Math.abs(s - ideal);
    if (d < bestDiff || (d === bestDiff && s < best)) {
      bestDiff = d;
      best = s;
    }
  }
  return best;
}

/**
 * Resolve a font + percent pair for a target effective scale.
 * In the linear model, effective scale depends only on percent.
 */
export function resolveSchemeZoomPairPreservingScale(
  _percentCommitted: number,
  effectiveTarget: number,
): { fontSize: SchemeFontSize; percent: number } {
  const percent = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, Math.round(effectiveTarget * 100));
  const fontSize = snapSchemeFontSize((percent / 100) * SCHEME_FONT_SIZE_DEFAULT);
  return { fontSize, percent };
}

/**
 * After the user changes zoom %, keep percent as the source of truth and
 * derive a matching font size (12 pt at 100%).
 */
export function schemeFontSizeAfterZoomChange(
  _prevFont: SchemeFontSize,
  _prevPercent: number,
  newPercent: number,
): { fontSize: SchemeFontSize; percent: number } {
  const percent = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, Math.round(newPercent));
  const fontIdeal = (percent / 100) * SCHEME_FONT_SIZE_DEFAULT;
  const fontSize = snapSchemeFontSize(fontIdeal);
  return { fontSize, percent };
}

/** Recover sidebar state from legacy single zoom (pre font+percent). */
export function schemeZoomFromLegacyDisplayZoom(zoom: number): {
  fontSize: SchemeFontSize;
  percent: number;
} {
  const z = Number(zoom);
  if (!Number.isFinite(z)) {
    return { fontSize: SCHEME_FONT_SIZE_DEFAULT, percent: SCHEME_ZOOM_PERCENT_DEFAULT };
  }
  const percent = clampSchemeZoomPercent(
    SCHEME_FONT_SIZE_DEFAULT,
    Math.round(z * 100),
  );
  return { fontSize: SCHEME_FONT_SIZE_DEFAULT, percent };
}
